import { Button, CircularProgress, Grid } from '@mui/material'
import { Chat, FolderShared, LocalHospital, Person, Place } from '@mui/icons-material'
import { Form, Formik, FormikHelpers } from 'formik'
import { inject } from 'mobx-react'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import Tab from '../common/Tabs/Tab'
import Tabs from '../common/Tabs/Tabs'
import Contacts from '../contacts/Contacts'
import { ICommunication, ILocation, IProvider } from '../Definitions'
import { ContactDto, ProviderTypeDto, TagDto } from '../generated_client'
import Locations from '../locations/Locations'
import Physicians from '../physicians/Physicians'
import { IStores } from '../Stores'
import * as Yup from 'yup'
import EditCommunicationsLog from './EditCommunicationsLog'
import ProviderDetails from './ProviderDetails'
import {
  createIProviderFormValues,
  IProviderFormValues,
  toIProvider,
} from './ProviderFormValues'
import { Helmet } from 'react-helmet'
import ChangeGroupModal from './ChangeGroupModal'
import { ClaimUpdateDueDateSummary } from '../viewModels/ClaimUpdateDueDateSummary'
import ProviderSaveStatusModal from './ProviderSaveStatusModal'

interface IEditProviderProps {
  providerId?: string
  provider?: IProvider
  providerTypes?: ProviderTypeDto[]
  loadProvider?: (id?: string) => Promise<void[]>
  saveProvider?: (
    provider: IProvider,
    stayInProviderPage?: boolean
  ) => ClaimUpdateDueDateSummary | any
  setSelectedProvider?: (provider: IProvider | undefined) => void
  setSelectedTags?: (tags: TagDto[]) => void
  communications?: ICommunication[]
  loadCommunications?: () => Promise<void>
  emptyLoadedCommunications?: () => void
  isAdmin?: boolean
  getImportantContacts?: (provider: IProvider) => Promise<void>
  getProviders?: (filter: string) => Promise<void>
  importantContacts?: ContactDto[]
  tags?: TagDto[]
  selectedTags?: TagDto[]
  getColumnSettingsAndLocations?: () => void
  locations?: ILocation[]
  isModal?: boolean
  getLocations?: () => Promise<void>
  getAllTags?: (filter: string) => void
  rerouteToPath?: (path: string) => void
  addSelectedTag?: (tag: TagDto) => void
  removeSelectedTag?: (tagId: string) => Promise<void>
  isLoading?: boolean
  openProviderSaveStatusModal?: (
    successCount?: number,
    errorClaimIds?: string[],
    isPaymentTierChanged?: boolean,
    providerSaveStatusModalOnClosing?: () => void
  ) => void
}
interface IEditProviderState {
  reload: boolean
  dirtyTags: boolean
  oldTags: string
}

class EditProvider extends React.Component<IEditProviderProps, IEditProviderState> {
  constructor(props: IEditProviderProps) {
    super(props)
    this.state = {
      reload: false,
      dirtyTags: false,
      oldTags: '',
    }
    this.forceUpdateHandler = this.forceUpdateHandler.bind(this)
  }
  public get title() {
    return this.props.providerId ? 'Edit Provider Group' : 'Add New Provider Group'
  }

  public componentDidMount() {
    ;(window as any).formikRef = React.createRef()
    this.setup()
    this.props.getAllTags!('')
  }

  public setup() {
    let promise
    const {
      loadCommunications,
      loadProvider,
      providerId,
      getImportantContacts,
      getColumnSettingsAndLocations,
      isModal,
      setSelectedProvider,
      getLocations,
    } = this.props
    if (providerId) {
      if (isModal) {
        setSelectedProvider!(undefined)
      }
      promise = loadProvider!(providerId)
    } else {
      promise = loadProvider!()
    }
    promise.then(() => {
      if (getImportantContacts && this.props.provider) {
        getImportantContacts(this.props.provider)
      }
      loadCommunications!()
      getColumnSettingsAndLocations!()
      getLocations!()
      this.props.setSelectedTags!(
        this.props.provider?.providerTags ?? new Array<TagDto>()
      )
      this.setState({ oldTags: JSON.stringify(this.props.selectedTags) })
    })
  }

  public componentWillUnmount() {
    if (!this.props.isModal) {
      this.props.setSelectedProvider!(undefined)
    }
  }

  public forceUpdateHandler() {
    var newTags = JSON.stringify(this.props.selectedTags)
    this.setState({ dirtyTags: newTags != this.state.oldTags })
  }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload }, () => {
      submitForm()
    })
  }

  public save = async (
    values: IProviderFormValues,
    formikBag: FormikHelpers<IProviderFormValues>
  ) => {
    formikBag.setSubmitting(true)
    const provider = await toIProvider(values, this.props.provider)
    provider.providerTags = this.props.selectedTags!

    var response = await this.props.saveProvider!(provider, true)

    formikBag.setSubmitting(false)
    this.props.openProviderSaveStatusModal!(
      response.successCount,
      response.errorClaimIds,
      response.isPaymentTierChanged,
      () => {
        if (!this.props.isModal) {
          if (this.state.reload) {
            this.props.rerouteToPath!('/providers/' + provider.id)
            this.setup()
          } else {
            window.close()
          }
        } else {
          window.close()
        }
      }
    )
  }

  public renderTabs() {
    if (this.props.providerId) {
      const tabs = [
        new Tab(<Locations providerId={this.props.providerId} />, 'Locations', <Place />),
        new Tab(<Physicians />, 'Physicians', <FolderShared />),
        new Tab(<Contacts />, 'Contacts', <Person />),
        new Tab(<EditCommunicationsLog />, 'Communication Log', <Chat />),
      ]
      return (
        <Tabs
          color="default"
          isHeaderElevated={false}
          isHeaderTransparent={true}
          tabColor="primary"
          fullWidth
          tabs={tabs}
        />
      )
    } else {
      return <></>
    }
  }

  public render() {
    const { provider, tags, importantContacts = [], isModal = false } = this.props
    const providerTypes = this.props.providerTypes || []
    return (
      <div>
        <ProviderSaveStatusModal />
        {this.props.isLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '2',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Helmet>
          <title>{this.title}</title>
        </Helmet>
        {provider ? (
          <Formik
            initialValues={createIProviderFormValues(provider, providerTypes)}
            onSubmit={this.save}
            validationSchema={this.buildSchema()}
            enableReinitialize={true}
            ref={(window as any).formikRef}
          >
            {({ errors, isValid, setFieldValue, values, submitForm }) => (
              <>
                {!isModal ? (
                  <>
                    <ChangeGroupModal />
                    <ResponsiveAppBar title={this.title} pageIcon={<LocalHospital />}>
                      <Grid
                        container
                        item
                        direction="row"
                        justifyContent="flex-end"
                        spacing={3}
                      >
                        <Grid item>
                          <Button
                            size="small"
                            variant="contained"
                            style={{ backgroundColor: '#e0e0e0', color: '#000' }}
                            onClick={() => window.close()}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            color="secondary"
                            size="small"
                            variant="contained"
                            disabled={!isValid && !this.state.dirtyTags}
                            onClick={this.handleSave(submitForm, true)}
                          >
                            Save
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            color="secondary"
                            size="small"
                            variant="contained"
                            disabled={!isValid && !this.state.dirtyTags}
                            onClick={this.handleSave(submitForm, false)}
                          >
                            Save and Close
                          </Button>
                        </Grid>
                      </Grid>
                    </ResponsiveAppBar>
                  </>
                ) : (
                  <></>
                )}
                <Form style={{ padding: '40px' }}>
                  <Grid container direction="column" spacing={3} wrap="nowrap">
                    <Grid item>
                      <ProviderDetails
                        importantContacts={importantContacts}
                        errors={errors}
                        key={values.id}
                        setFieldValue={setFieldValue}
                        providerTypes={providerTypes}
                        values={values}
                        tags={tags}
                        isNew={!this.props.providerId}
                        selectedTags={this.props.selectedTags}
                        addSelectedTag={this.props.addSelectedTag}
                        removeSelectedTag={this.props.removeSelectedTag}
                        forceUpdate={this.forceUpdateHandler}
                      />
                    </Grid>
                    {isModal ? (
                      <Grid container item justifyContent="flex-end" spacing={3}>
                        <Grid item>
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={!isValid}
                            onClick={submitForm}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Form>
                <Grid
                  container
                  item
                  direction="row"
                  justifyContent="flex-start"
                  style={{ padding: '40px' }}
                >
                  {this.renderTabs()}
                </Grid>
              </>
            )}
          </Formik>
        ) : (
          <></>
        )}
      </div>
    )
  }

  private buildSchema() {
    return Yup.object().shape({
      isSingleLocation: Yup.boolean().default(false).required(),
      name: Yup.string().required('Name is required'),
      contractLink: Yup.string().url('Link must start with http://, https://, or ftp://'),
    })
  }
}

const InjectedEditProvider = inject<
  IStores,
  IEditProviderProps,
  Partial<IEditProviderProps>,
  any
>((stores: IStores) => ({
  getColumnSettingsAndLocations: stores.locations.getColumnSettingsAndLocations,
  getImportantContacts: stores.contacts.getImportantContacts,
  getLocations: stores.locations.getAllLocationsForProvider,
  getOrganizations: stores.organizations.getAllOrganizations,
  getProviders: stores.providers.getProviderSuggestions,
  getAllTags: stores.tags.getAllTags,
  importantContacts: stores.contacts.importantContacts,
  isAdmin: stores.global.isAdmin,
  loadCommunications: stores.communications.getColumnSettingsAndFullCommunications,
  loadProvider: stores.providers.loadProvider,
  locations: stores.locations.locations,
  provider: stores.providers.selectedProvider,
  providerTypes: stores.providers.providerTypes,
  rerouteToPath: stores.global.rerouteToPath,
  saveProvider: stores.providers.saveProvider,
  setSelectedProvider: stores.providers.setSelectedProvider,
  openProviderSaveStatusModal: stores.providers.openProviderSaveStatusModal,
  tags: stores.tags.tags,
  selectedTags: stores.locations.selectedTags,
  setSelectedTags: stores.locations.setSelectedTags,
  removeSelectedTag: stores.locations.removeSelectedTag,
  addSelectedTag: stores.locations.addSelectedTag,
  isLoading: stores.providers.isLoading,
}))(EditProvider)

const EditProviderWithParams = (props: any) => {
  const { providerId } = useParams<{ providerId?: string }>()
  return <InjectedEditProvider {...props} providerId={providerId} />
}

export default EditProviderWithParams
